import { Permiso } from "@/shared/types/permiso";
import { Stack } from "@mui/material";
import { useMemo } from "react";
import { permisosConfig } from "../shared/permisos-config";
import { z } from "zod/v4";
import { Form, FormProps } from "@/components/form";
import { useOperationalMode } from "@/features/configuration/hooks/use-operational-mode";
import { OperationalMode } from "@/shared/types/operational-mode";

export const editUsuarioPermisosFormSchema = z.object({
    permisos: z.record(z.enum(Permiso), z.boolean())
})

const HIDDEN_IN_FACTURACION = new Set([
    "Inventario",
    "Caja",
    "Reportes",
    "Manejo de Rubros",
    "Manejo de Proveedores",
    "Manejo de Vendedores",
    "Manejo de Cuenta Corrientes",
]);

export default function EditUsuarioPermisosForm({
    form,
    ...props
}: FormProps<z.infer<typeof editUsuarioPermisosFormSchema>>) {
    const { facturacionEnabled, mode } = useOperationalMode();
    const isFacturacionMode = mode === OperationalMode.Facturacion;

    const filteredPermisosConfig = useMemo(() => {
        let config = permisosConfig as typeof permisosConfig[number][];

        if (!facturacionEnabled) {
            config = config.filter((p) =>
                !("content" in p && (p.name === "Facturación Electrónica" || p.name === "Actividades Comerciales"))
            );
        }

        if (isFacturacionMode) {
            config = config
                .filter((p) => !("content" in p && HIDDEN_IN_FACTURACION.has(p.name)))
                .map((p) => {
                    if (!("content" in p) || p.name !== "Auxiliares") return p;
                    return {
                        ...p,
                        content: p.content.filter((sub) => !("content" in sub && HIDDEN_IN_FACTURACION.has(sub.name)))
                    };
                });
        }

        return config;
    }, [facturacionEnabled, isFacturacionMode]);

    const render = useMemo(() => {
        type Unpacked<T> = T extends (infer U)[] ? U : T;
        type PermisosListContent = Unpacked<typeof permisosConfig>;
        type PermisoListEntry = Extract<PermisosListContent, { value: string }>;
        type PermisoListCategory = Extract<PermisosListContent, { content: any[] }>

        const handleRenderEntry = (entry: PermisoListEntry) => {
            return <Form.CheckBox key={entry.value} label={entry.name} name={"permisos." + entry.value} />
        }

        const handleRenderCategory = (category: PermisoListCategory) => {
            return <Form.CheckBoxGroup key={category.name} label={category.name} variant="collapse">
                {category.content.map(p => ("content" in p) ?
                    handleRenderCategory(p) :
                    handleRenderEntry(p))}
            </Form.CheckBoxGroup>
        }

        return <Stack gap={1}>
            {filteredPermisosConfig.map(p => ("content" in p) ?
                handleRenderCategory(p as PermisoListCategory) :
                handleRenderEntry(p as PermisoListEntry))}
        </Stack>
    }, [filteredPermisosConfig])

    return <Form form={form} gap={3} {...props}>
        {render}
        <Form.SubmitButton>Guardar</Form.SubmitButton>
    </Form>
}